/**
 * CR283: Merchant Overview Card Component
 * CR403: Updated to use merchant_categories table via dropdown
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save, Loader2 } from "lucide-react";
import type { MerchantDirectoryRow } from "@/hooks/useMerchantsDirectory";
import { useMerchantDirectoryMutations } from "@/hooks/useMerchantsDirectory";
import { useMerchantCategoriesList, useMerchantCategoryMutations } from "@/hooks/useMerchantCategories";

const MERCHANT_TYPES = [
  { value: "issuer", label: "Issuer" },
  { value: "retailer", label: "Retailer" },
  { value: "both", label: "Both" },
];

const STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "active", label: "Active" },
  { value: "hidden", label: "Hidden" },
];

interface MerchantOverviewCardProps {
  merchant: MerchantDirectoryRow;
}

export function MerchantOverviewCard({ merchant }: MerchantOverviewCardProps) {
  const [displayName, setDisplayName] = useState(merchant.display_name);
  const [category, setCategory] = useState(merchant.category);
  const [merchantType, setMerchantType] = useState(merchant.merchant_type);
  const [directoryStatus, setDirectoryStatus] = useState(merchant.directory_status);

  const { updateDetails, isUpdatingDetails } = useMerchantDirectoryMutations();
  const { data: categories = [] } = useMerchantCategoriesList();

  const hasChanges =
    displayName !== merchant.display_name ||
    category !== merchant.category ||
    merchantType !== merchant.merchant_type ||
    directoryStatus !== merchant.directory_status;

  const handleSave = async () => {
    await updateDetails({
      merchantKey: merchant.merchant_key,
      overview: {
        display_name: displayName,
        category,
        merchant_type: merchantType,
        directory_status: directoryStatus,
      },
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Overview</CardTitle>
        <CardDescription>
          Basic merchant information. Merchant key is read-only.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Merchant Key</Label>
          <Input value={merchant.merchant_key} disabled className="font-mono" />
        </div>

        <div className="space-y-2">
          <Label>Display Name</Label>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.name}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Merchant Type</Label>
            <Select value={merchantType} onValueChange={setMerchantType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MERCHANT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Directory Status</Label>
          <Select value={directoryStatus} onValueChange={(v) => setDirectoryStatus(v as typeof directoryStatus)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          onClick={handleSave}
          disabled={!hasChanges || isUpdatingDetails}
          className="w-full"
        >
          {isUpdatingDetails ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save Overview
        </Button>
      </CardContent>
    </Card>
  );
}
